/**
 * 二维码登录 Composable
 * 封装完整的二维码登录流程
 */

import { ref, computed, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { AuthService } from '../core/services/AuthService'

/** 二维码状态 */
export type QRCodeStatus = 'loading' | 'ready' | 'scanned' | 'confirmed' | 'expired' | 'error'

export interface UseQRLoginOptions {
  /** 登录成功回调 */
  onLoginSuccess?: () => void | Promise<void>
  /** 二维码过期回调 */
  onExpired?: () => void
  /** 错误回调 */
  onError?: (error: Error) => void
  /** 是否自动开始，默认 true */
  autoStart?: boolean
}

export interface UseQRLoginReturn {
  /** 二维码图片 URL */
  qrCodeUrl: Ref<string>
  /** 二维码状态 */
  status: Ref<QRCodeStatus>
  /** 是否已过期 */
  isExpired: ComputedRef<boolean>
  /** 是否正在加载 */
  isLoading: ComputedRef<boolean>
  /** 是否可以刷新 */
  canRefresh: ComputedRef<boolean>
  /** 开始二维码登录 */
  start: () => Promise<void>
  /** 刷新二维码 */
  refresh: () => Promise<void>
  /** 停止轮询 */
  stop: () => void
}

/**
 * 创建二维码登录 Composable
 * @param authService 认证服务实例
 */
export function createUseQRLogin(authService: AuthService) {
  return function useQRLogin(options: UseQRLoginOptions = {}): UseQRLoginReturn {
    const {
      onLoginSuccess,
      onExpired,
      onError,
      autoStart = true
    } = options

    // 状态
    const qrCodeUrl = ref('')
    const status = ref<QRCodeStatus>('loading')

    // 计算属性
    const isExpired = computed(() => status.value === 'expired')
    const isLoading = computed(() => status.value === 'loading')
    const canRefresh = computed(() =>
      status.value === 'expired' || status.value === 'error'
    )

    // 开始二维码登录
    const start = async () => {
      status.value = 'loading'
      qrCodeUrl.value = ''

      try {
        const qrCodeInfo = await authService.startQRLogin((newStatus) => {
          if (newStatus === 'confirmed') {
            status.value = 'confirmed'
            onLoginSuccess?.()
          } else if (newStatus === 'expired') {
            status.value = 'expired'
            onExpired?.()
          } else if (newStatus === 'scanned') {
            status.value = 'scanned'
          }
        })

        // 设置二维码 URL
        qrCodeUrl.value = qrCodeInfo.url || qrCodeInfo.content || ''
        status.value = 'ready'
      } catch (error) {
        console.error('[useQRLogin] 获取二维码失败:', error)
        status.value = 'error'
        onError?.(error as Error)
      }
    }

    // 刷新二维码
    const refresh = async () => {
      // 先停止之前的轮询
      authService.stopQRLogin()
      // 重新开始
      await start()
    }

    // 停止轮询
    const stop = () => {
      authService.stopQRLogin()
      qrCodeUrl.value = ''
      status.value = 'loading'
    }

    // 自动开始
    if (autoStart) {
      start()
    }

    // 组件卸载时清理
    onUnmounted(() => {
      stop()
    })

    return {
      qrCodeUrl,
      status,
      isExpired,
      isLoading,
      canRefresh,
      start,
      refresh,
      stop
    }
  }
}
